import { Link } from "react-router-dom";
import { Receipt, RotateCcw, ShieldCheck, Users, ArrowRight } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

const content = {
  fr: {
    eyebrow: "Plus qu'un scan de reçus",
    heading: "Des preuves d'achat intelligentes pour la vraie vie",
    description:
      "ReFound ne garde pas seulement une photo. Il transforme vos reçus en preuves d'achat intelligentes, enrichies avec les informations utiles pour les retours, les garanties, le partage familial et la recherche.",
    cards: [
      {
        icon: Receipt,
        title: "Application de reçus",
        desc: "Scannez, enrichissez, organisez et retrouvez vos reçus.",
        href: "/application-recus",
      },
      {
        icon: RotateCcw,
        title: "Retours en magasin",
        desc: "Présentez le bon reçu, avec le code-barres quand disponible.",
        href: "/application-retours-magasin",
      },
      {
        icon: ShieldCheck,
        title: "Garanties et preuves d'achat",
        desc: "Conservez vos reçus et garanties au même endroit.",
        href: "/application-garanties",
      },
      {
        icon: Users,
        title: "Reçus pour la famille",
        desc: "Partagez un reçu avec un conjoint ou un membre du foyer.",
        href: "/application-recus-famille",
      },
    ],
    learnMore: "En savoir plus",
  },
  en: {
    eyebrow: "More than receipt scanning",
    heading: "Smart proof of purchase for real life",
    description:
      "ReFound doesn't just keep a photo. It turns receipts into smart proof of purchase, enriched with useful information for returns, warranties, family sharing and search.",
    cards: [
      {
        icon: Receipt,
        title: "Receipt app",
        desc: "Scan, enrich, organize and find your receipts.",
        href: "/en/receipt-app",
      },
      {
        icon: RotateCcw,
        title: "In-store returns",
        desc: "Present the right receipt, with the barcode when available.",
        href: "/en/returns-app",
      },
      {
        icon: ShieldCheck,
        title: "Warranties & proof of purchase",
        desc: "Keep your receipts and warranties in one place.",
        href: "/en/warranty-tracker-app",
      },
      {
        icon: Users,
        title: "Family receipts",
        desc: "Share a receipt with a partner or household member.",
        href: "/en/family-receipt-app",
      },
    ],
    learnMore: "Learn more",
  },
};

export function HomeSolutions() {
  const { language } = useLanguage();
  const c = content[language];

  return (
    <section className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-primary mb-4">
            {c.eyebrow}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            {c.heading}
          </h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            {c.description}
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.cards.map((card) => {
            const Icon = card.icon;
            return (
              <Link
                key={card.href}
                to={card.href}
                className="group rounded-2xl border border-border/50 bg-card/40 p-6 transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
              >
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {card.desc}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  {c.learnMore}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
